import maplibregl from 'maplibre-gl';
import { fetchWindGrid, WindGridData } from './WindGridLoader';
import store, { getLayerOpacity } from '../state';

export type WindLayerProfile = 'surface' | 'jet_stream';

interface CanvasWindParticle {
  lon: number;
  lat: number;
  age: number;
  maxAge: number;
}

// Speed color gradient matching baseline for surface winds (0 - 25 m/s)
function getWindColor(speed: number, opacity: number): string {
  const stops = [
    { val: 0, r: 0, g: 80, b: 255 },
    { val: 5, r: 34, g: 197, b: 94 },
    { val: 10, r: 234, g: 179, b: 8 },
    { val: 15, r: 249, g: 115, b: 22 },
    { val: 20, r: 239, g: 68, b: 68 },
    { val: 25, r: 217, g: 70, b: 239 }
  ];

  if (speed <= stops[0].val) {
    return `rgba(${stops[0].r}, ${stops[0].g}, ${stops[0].b}, ${opacity})`;
  }
  if (speed >= stops[stops.length - 1].val) {
    return `rgba(${stops[stops.length - 1].r}, ${stops[stops.length - 1].g}, ${stops[stops.length - 1].b}, ${opacity})`;
  }

  let i = 0;
  while (i < stops.length - 1 && speed > stops[i + 1].val) {
    i++;
  }

  const s0 = stops[i];
  const s1 = stops[i + 1];
  const t = (speed - s0.val) / (s1.val - s0.val);

  const r = Math.round(s0.r + (s1.r - s0.r) * t);
  const g = Math.round(s0.g + (s1.g - s0.g) * t);
  const b = Math.round(s0.b + (s1.b - s0.b) * t);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Jet stream & winds aloft 250 hPa color palette matching isotach legend:
// 50 kt (~25.7 m/s): #38bdf8 (Sky Blue)
// 75 kt (~38.6 m/s): #3b82f6 (Blue)
// 100 kt (~51.4 m/s): #8b5cf6 (Purple)
// 130 kt (~66.9 m/s): #ec4899 (Pink)
// 160+ kt (~82.3 m/s): #ef4444 (Crimson Red)
function getJetStreamWindColor(speed: number, opacity: number): string {
  const stops = [
    { val: 0, r: 56, g: 189, b: 248 },
    { val: 25.7, r: 56, g: 189, b: 248 },
    { val: 38.6, r: 59, g: 130, b: 246 },
    { val: 51.4, r: 139, g: 92, b: 246 },
    { val: 66.9, r: 236, g: 72, b: 153 },
    { val: 82.3, r: 239, g: 68, b: 68 }
  ];

  if (speed <= stops[0].val) {
    return `rgba(${stops[0].r}, ${stops[0].g}, ${stops[0].b}, ${opacity})`;
  }
  if (speed >= stops[stops.length - 1].val) {
    return `rgba(${stops[stops.length - 1].r}, ${stops[stops.length - 1].g}, ${stops[stops.length - 1].b}, ${opacity})`;
  }

  let i = 0;
  while (i < stops.length - 1 && speed > stops[i + 1].val) {
    i++;
  }

  const s0 = stops[i];
  const s1 = stops[i + 1];
  const t = (speed - s0.val) / (s1.val - s0.val);

  const r = Math.round(s0.r + (s1.r - s0.r) * t);
  const g = Math.round(s0.g + (s1.g - s0.g) * t);
  const b = Math.round(s0.b + (s1.b - s0.b) * t);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Precomputed color tables for color-bucketed particle batching
const BUCKET_COLORS_CACHE: Record<string, string[]> = {};
function getBucketColors(numBuckets: number, profile: WindLayerProfile = 'surface'): string[] {
  const cacheKey = `${profile}_${numBuckets}`;
  if (!BUCKET_COLORS_CACHE[cacheKey]) {
    const colors: string[] = [];
    const maxSpeed = profile === 'jet_stream' ? 82.3 : 25.0;
    for (let i = 0; i < numBuckets; i++) {
      const speed = ((i + 0.5) / numBuckets) * maxSpeed;
      colors.push(profile === 'jet_stream' ? getJetStreamWindColor(speed, 1.0) : getWindColor(speed, 1.0));
    }
    BUCKET_COLORS_CACHE[cacheKey] = colors;
  }
  return BUCKET_COLORS_CACHE[cacheKey];
}

// Web Mercator (EPSG:3857) normalized Y coordinate [0, 1] matching MapLibre GL
function latToMercatorY(lat: number): number {
  const clampedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sin = Math.sin((clampedLat * Math.PI) / 180.0);
  const clampedSin = Math.max(-0.999999, Math.min(0.999999, sin));
  return 0.5 - (Math.atanh(clampedSin) / (2.0 * Math.PI));
}

// Inverse Web Mercator: converts normalized Y [0, 1] back to geodetic latitude in degrees
function mercatorYtoLat(y: number): number {
  const m = Math.PI * (1.0 - 2.0 * y);
  return (Math.asin(Math.tanh(m)) * 180.0) / Math.PI;
}

export class CanvasWindRenderer {
  private map: maplibregl.Map;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  // Dual offscreen ping-pong buffers for seamless periodic zero-alpha reset
  private bufferA: HTMLCanvasElement | null = null;
  private ctxA: CanvasRenderingContext2D | null = null;
  private bufferB: HTMLCanvasElement | null = null;
  private ctxB: CanvasRenderingContext2D | null = null;

  private activeBuffer: 'A' | 'B' = 'A';
  private cycleStartTime = Date.now();
  private readonly cycleDuration = 30000; // 30s cycle
  private readonly crossfadeDuration = 2500; // 2.5s transition
  
  private sourceId: string;
  private layerId: string;
  private profile: WindLayerProfile;
  
  private windGridData: WindGridData | null = null;
  private particlesA: CanvasWindParticle[] = [];
  private particlesB: CanvasWindParticle[] = [];
  private isRunning = false;
  private currentGridUrl = '';
  private refreshGeneration = 0;
  private animationFrameId: number | null = null;
  private unsubscribeStore: (() => void) | null = null;
  private lastFrameTime = 0;
  private bucketSegments: number[][] = [];
  
  private opacity = 0.80;
  private displayMode: 'average' | 'gusts' = 'average';

  constructor(
    map: maplibregl.Map,
    sourceId: string = 'rtma-wind-canvas-source',
    layerId: string = 'rtma-wind-canvas-layer',
    profile?: WindLayerProfile
  ) {
    this.map = map;
    this.sourceId = sourceId;
    this.layerId = layerId;
    this.profile = profile || (sourceId.includes('jet-stream') ? 'jet_stream' : 'surface');

    this.animate = this.animate.bind(this);
    
    // Main presentation canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1600;
    this.canvas.height = 1000;
    this.ctx = this.canvas.getContext('2d');

    // Dual ping-pong buffers
    this.bufferA = document.createElement('canvas');
    this.bufferA.width = 1600;
    this.bufferA.height = 1000;
    this.ctxA = this.bufferA.getContext('2d');

    this.bufferB = document.createElement('canvas');
    this.bufferB.width = 1600;
    this.bufferB.height = 1000;
    this.ctxB = this.bufferB.getContext('2d');

    this.map.on('movestart', () => {
      if (this.windGridData && this.isRunning) {
        this.clearAllBuffers();
      }
    });

    this.map.on('move', () => {
      const state = store.getState();
      const isPlaying = this.profile === 'jet_stream'
        ? (state.jetStreamParticlesPlaying !== false)
        : (state.windParticlesPlaying !== false);
      if (this.isRunning && this.windGridData && !isPlaying) {
        this.drawFrame();
      }
    });

    this.setupStoreSubscription();
  }

  private setupStoreSubscription() {
    if (this.unsubscribeStore) return;

    let lastPlaying = true;
    let lastVisible = true;
    let lastColor = 'speed';
    let lastSize = 'normal';
    let lastTrail = 'medium';
    let lastDensity = 'medium';
    let lastSpeed = 0.15;
    let lastMode = 'average';
    let lastOpacity = 0.80;
    let lastThrottle = 'unlimited';
    let lastBatch = 'high';
    let lastRes = 'high';

    this.unsubscribeStore = store.subscribe((newState) => {
      if (!this.isRunning) return;

      const throttleMode = newState.windAnimationThrottle || 'unlimited';
      const batchMode = newState.windColorBatching || 'high';
      const resMode = newState.windCanvasResolution || 'high';

      let isPlaying: boolean;
      let isVisible: boolean;
      let colorMode: string;
      let sizeMode: string;
      let trailMode: string;
      let densityMode: string;
      let speed: number;
      let mode: 'average' | 'gusts';
      let opacity: number;

      if (this.profile === 'jet_stream') {
        isPlaying = newState.jetStreamParticlesPlaying !== false;
        isVisible = newState.jetStreamParticlesVisible !== false &&
                    newState.showJetStreamWinds !== false &&
                    newState.jetStreamDisplayMode !== 'raster';
        colorMode = newState.jetStreamColorMode || 'speed';
        sizeMode = newState.jetStreamParticleSize || 'normal';
        trailMode = newState.jetStreamTrailLength || 'medium';
        densityMode = newState.jetStreamParticleDensityLevel || 'medium';
        speed = newState.jetStreamSpeed || 0.15;
        mode = 'average';
        opacity = getLayerOpacity('jet_stream', 0.80);
      } else {
        isPlaying = newState.windParticlesPlaying !== false;
        isVisible = newState.windParticlesVisible !== false;
        colorMode = newState.windColorMode || 'speed';
        sizeMode = newState.windParticleSize || 'normal';
        trailMode = newState.windTrailLength || 'medium';
        densityMode = newState.windParticleDensityLevel || 'medium';
        speed = newState.windSpeed || 0.15;
        mode = newState.liveWeatherRtmaWindsMode || 'average';
        opacity = newState.liveWeatherRtmaWindsOpacity ?? 0.80;
      }

      if (opacity !== lastOpacity) {
        lastOpacity = opacity;
        this.setOpacity(opacity);
      }

      // Handle play/pause toggling
      if (isPlaying !== lastPlaying) {
        lastPlaying = isPlaying;
        if (isPlaying) {
          if (!this.animationFrameId) this.animate();
        } else {
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
          this.drawFrame();
        }
      }

      // Handle visibility toggle
      if (isVisible !== lastVisible) {
        lastVisible = isVisible;
        if (!isVisible) {
          this.clearAllBuffers();
          try {
            this.map.triggerRepaint();
          } catch (_e) {}
        } else {
          this.drawFrame();
        }
      }

      // Handle visual & performance variations trigger redraw
      if (
        colorMode !== lastColor ||
        sizeMode !== lastSize ||
        trailMode !== lastTrail ||
        densityMode !== lastDensity ||
        speed !== lastSpeed ||
        mode !== lastMode ||
        throttleMode !== lastThrottle ||
        batchMode !== lastBatch ||
        resMode !== lastRes
      ) {
        lastColor = colorMode;
        lastSize = sizeMode;
        lastTrail = trailMode;
        lastDensity = densityMode;
        lastSpeed = speed;
        lastMode = mode;
        lastThrottle = throttleMode;
        lastBatch = batchMode;
        this.displayMode = mode;
        if (resMode !== lastRes) {
          lastRes = resMode;
          this.clearAllBuffers();
        }
        if (!isPlaying) {
          this.drawFrame();
        }
      }
    });
  }

  private clearAllBuffers() {
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    if (this.ctxA && this.bufferA) {
      this.ctxA.clearRect(0, 0, this.bufferA.width, this.bufferA.height);
    }
    if (this.ctxB && this.bufferB) {
      this.ctxB.clearRect(0, 0, this.bufferB.width, this.bufferB.height);
    }
  }

  public setOpacity(opacity: number) {
    this.opacity = opacity;
    this.updateLayerOpacity();
  }

  public setDisplayMode(mode: 'average' | 'gusts') {
    this.displayMode = mode;
  }

  public async setGridUrl(gridUrl: string, opacity: number = 0.80, displayMode: 'average' | 'gusts' = 'average'): Promise<void> {
    this.opacity = opacity;
    this.displayMode = displayMode;

    if (this.currentGridUrl === gridUrl && this.windGridData) {
      this.updateLayerOpacity();
      return;
    }

    this.currentGridUrl = gridUrl;
    const gen = ++this.refreshGeneration;

    try {
      const grid = await fetchWindGrid(gridUrl);
      if (gen !== this.refreshGeneration) return;

      if (grid && grid.u && grid.v && grid.u.length > 0) {
        this.windGridData = grid;

        
        const state = store.getState();
        const baseDensity = this.profile === 'jet_stream'
          ? (state.jetStreamDensity || 4500)
          : (state.windDensity || 4500);
        let targetDensity = baseDensity;
        const densityLevel = this.profile === 'jet_stream'
          ? state.jetStreamParticleDensityLevel
          : state.windParticleDensityLevel;
        if (densityLevel === 'low') targetDensity = 2200;
        else if (densityLevel === 'high') targetDensity = 7500;

        this.particlesA = this.createParticles(targetDensity);
        this.particlesB = [];
        this.clearAllBuffers();
        this.ensureLayerPosition();
        if (!this.isRunning) {
          this.startAnimation();
        }
      }
    } catch (e) {
      if (gen === this.refreshGeneration) {
        console.warn("CanvasWindRenderer: Failed to fetch grid from", gridUrl, e);
        this.clear();
      }
    }
  }

  private createParticles(density: number): CanvasWindParticle[] {
    if (!this.windGridData) return [];
    const particles: CanvasWindParticle[] = [];

    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());
    const isGlobal = (this.windGridData.east - this.windGridData.west) >= 359;

    let actualWest: number;
    let actualEast: number;
    let actualNorth = Math.min(viewportNorth, this.windGridData.north);
    let actualSouth = Math.max(viewportSouth, this.windGridData.south);

    if (isGlobal) {
      actualWest = viewportWest;
      actualEast = viewportEast;
    } else {
      const gridWest = this.windGridData.west;
      const gridEast = this.windGridData.east;
      const gridCenter = (gridWest + gridEast) / 2;
      const vpCenter = (viewportWest + viewportEast) / 2;
      const worldOffset = Math.round((vpCenter - gridCenter) / 360) * 360;

      const shiftedWest = gridWest + worldOffset;
      const shiftedEast = gridEast + worldOffset;

      const west = Math.max(viewportWest, shiftedWest);
      const east = Math.min(viewportEast, shiftedEast);
      actualWest = east > west ? west : shiftedWest;
      actualEast = east > west ? east : shiftedEast;
    }

    if (actualNorth <= actualSouth) {
      actualNorth = this.windGridData.north;
      actualSouth = this.windGridData.south;
    }

    const mNorth = latToMercatorY(actualNorth);
    const mSouth = latToMercatorY(actualSouth);

    for (let i = 0; i < density; i++) {
      let pLon = actualWest + Math.random() * (actualEast - actualWest);
      let mY = mNorth + Math.random() * (mSouth - mNorth);
      let pLat = mercatorYtoLat(mY);

      // Attempt to land on active wind cell (avoid dead calm/masked regions)
      for (let attempt = 0; attempt < 4; attempt++) {
        const w = this.getWindAt(pLon, pLat);
        if (w && w.speed >= 0.2) break;
        pLon = actualWest + Math.random() * (actualEast - actualWest);
        mY = mNorth + Math.random() * (mSouth - mNorth);
        pLat = mercatorYtoLat(mY);
      }

      particles.push({
        lon: pLon,
        lat: pLat,
        age: Math.random() * 100,
        maxAge: 50 + Math.random() * 100
      });
    }
    return particles;
  }

  private respawnParticle(p: CanvasWindParticle, vWest: number, vEast: number, vNorth: number, vSouth: number) {
    if (!this.windGridData) return;
    const isGlobal = (this.windGridData.east - this.windGridData.west) >= 359;

    let targetLon = vWest;
    let targetLat = vSouth;

    for (let attempt = 0; attempt < 5; attempt++) {
      if (isGlobal) {
        targetLon = vWest + Math.random() * (vEast - vWest);
        const north = Math.min(vNorth, this.windGridData.north);
        const south = Math.max(vSouth, this.windGridData.south);
        const actualN = south < north ? north : vNorth;
        const actualS = south < north ? south : vSouth;
        const mN = latToMercatorY(actualN);
        const mS = latToMercatorY(actualS);
        targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
      } else {
        const gridWest = this.windGridData.west;
        const gridEast = this.windGridData.east;
        const gridCenter = (gridWest + gridEast) / 2;
        const vpCenter = (vWest + vEast) / 2;
        const worldOffset = Math.round((vpCenter - gridCenter) / 360) * 360;

        const shiftedWest = gridWest + worldOffset;
        const shiftedEast = gridEast + worldOffset;

        const west = Math.max(vWest, shiftedWest);
        const east = Math.min(vEast, shiftedEast);
        const north = Math.min(vNorth, this.windGridData.north);
        const south = Math.max(vSouth, this.windGridData.south);

        if (east > west && north > south) {
          targetLon = west + Math.random() * (east - west);
          const mN = latToMercatorY(north);
          const mS = latToMercatorY(south);
          targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
        } else {
          targetLon = shiftedWest + Math.random() * (shiftedEast - shiftedWest);
          const mN = latToMercatorY(this.windGridData.north);
          const mS = latToMercatorY(this.windGridData.south);
          targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
        }
      }

      const w = this.getWindAt(targetLon, targetLat);
      if (w && w.speed >= 0.2) break;
    }

    p.lon = targetLon;
    p.lat = Math.max(-85.05112878, Math.min(85.05112878, targetLat));
    p.age = 0;
    p.maxAge = 50 + Math.random() * 100;
  }

  public startAnimation() {
    this.isRunning = true;
    this.setupStoreSubscription();
    const isPlaying = this.profile === 'jet_stream'
      ? (store.getState().jetStreamParticlesPlaying !== false)
      : (store.getState().windParticlesPlaying !== false);
    if (isPlaying) {
      if (this.animationFrameId === null) {
        this.animate();
      }
    } else {
      this.drawFrame();
    }
  }

  public stopAnimation() {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private animate() {
    if (!this.isRunning || !this.windGridData) {
      this.animationFrameId = null;
      return;
    }

    const state = store.getState();
    const isPlaying = this.profile === 'jet_stream'
      ? (state.jetStreamParticlesPlaying !== false)
      : (state.windParticlesPlaying !== false);
    if (!isPlaying) {
      this.animationFrameId = null;
      return;
    }

    if (state.windAnimationThrottle === '30fps') {
      const now = performance.now();
      const elapsed = now - this.lastFrameTime;
      const frameInterval = 33.33; // 30 FPS target (33.33ms)
      if (elapsed < frameInterval) {
        this.animationFrameId = requestAnimationFrame(this.animate);
        return;
      }
      this.lastFrameTime = now - (elapsed % frameInterval);
    }

    this.drawFrame();
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  private stepBuffer(
    bufferCtx: CanvasRenderingContext2D,
    particles: CanvasWindParticle[],
    targetDensity: number,
    width: number,
    height: number,
    viewportWest: number,
    viewportEast: number,
    viewportNorth: number,
    viewportSouth: number,
    vpWidth: number,
    mercNorthY: number,
    mercHeight: number,
    speedMultiplier: number,
    lineWidth: number,
    fadeAlpha: number,
    isPlaying: boolean,
    isWhiteMode: boolean,
    batchMode: 'low' | 'med' | 'high' = 'high'
  ) {
    if (particles.length !== targetDensity) {
      const fresh = this.createParticles(targetDensity);
      particles.length = 0;
      for (let i = 0; i < fresh.length; i++) particles.push(fresh[i]);
    }

    // Natural exponential trail decay matching baseline
    bufferCtx.globalCompositeOperation = 'destination-out';
    bufferCtx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha.toFixed(4)})`;
    bufferCtx.fillRect(0, 0, width, height);
    bufferCtx.globalCompositeOperation = 'source-over';

    bufferCtx.lineWidth = lineWidth;
    bufferCtx.lineCap = 'round';
    bufferCtx.lineJoin = 'round';

    const dt = 0.6;
    const speedScale = 4000.0;

    if (isWhiteMode) {
      bufferCtx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
      bufferCtx.beginPath();
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (isPlaying) {
          p.age++;
          if (
            p.age > p.maxAge ||
            p.lon < viewportWest || p.lon > viewportEast ||
            p.lat < viewportSouth || p.lat > viewportNorth
          ) {
            this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            continue;
          }
          const wind = this.getWindAt(p.lon, p.lat);
          if (!wind || wind.speed < 0.2) {
            if (!wind || p.age > 10) {
              this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            }
            continue;
          }

          const x1 = ((p.lon - viewportWest) / vpWidth) * width;
          const y1 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          const lat_step = (wind.v / 111000.0) * speedMultiplier * dt * speedScale;
          const lon_step = (wind.u / (111000.0 * Math.cos((p.lat * Math.PI) / 180.0))) * speedMultiplier * dt * speedScale;

          p.lon += lon_step;
          p.lat = Math.max(-85.05112878, Math.min(85.05112878, p.lat + lat_step));

          const x2 = ((p.lon - viewportWest) / vpWidth) * width;
          const y2 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;
          if (Math.abs(x2 - x1) < 0.1 && Math.abs(y2 - y1) < 0.1) continue;

          bufferCtx.moveTo(x1, y1);
          bufferCtx.lineTo(x2, y2);
        }
      }
      bufferCtx.stroke();
    } else {
      // Color-bucketed particle batching: low = 8 colors, med = 16 colors, high = 64 colors
      const numBuckets = batchMode === 'low' ? 8 : (batchMode === 'med' ? 16 : 64);
      for (let b = 0; b < numBuckets; b++) {
        if (!this.bucketSegments[b]) this.bucketSegments[b] = [];
        this.bucketSegments[b].length = 0;
      }

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (isPlaying) {
          p.age++;
          if (
            p.age > p.maxAge ||
            p.lon < viewportWest || p.lon > viewportEast ||
            p.lat < viewportSouth || p.lat > viewportNorth
          ) {
            this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            continue;
          }
          const wind = this.getWindAt(p.lon, p.lat);
          if (!wind || wind.speed < 0.2) {
            if (!wind || p.age > 10) {
              this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            }
            continue;
          }

          const x1 = ((p.lon - viewportWest) / vpWidth) * width;
          const y1 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          const lat_step = (wind.v / 111000.0) * speedMultiplier * dt * speedScale;
          const lon_step = (wind.u / (111000.0 * Math.cos((p.lat * Math.PI) / 180.0))) * speedMultiplier * dt * speedScale;

          p.lon += lon_step;
          p.lat = Math.max(-85.05112878, Math.min(85.05112878, p.lat + lat_step));

          const x2 = ((p.lon - viewportWest) / vpWidth) * width;
          const y2 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;
          if (Math.abs(x2 - x1) < 0.1 && Math.abs(y2 - y1) < 0.1) continue;

          // Assign segment to speed bucket
          const maxSpeed = this.profile === 'jet_stream' ? 82.3 : 25.0;
          const bIdx = Math.max(0, Math.min(numBuckets - 1, Math.floor((wind.speed / maxSpeed) * numBuckets)));
          this.bucketSegments[bIdx].push(x1, y1, x2, y2);
        }
      }

      const colors = getBucketColors(numBuckets, this.profile);
      for (let b = 0; b < numBuckets; b++) {
        const seg = this.bucketSegments[b];
        const segLen = seg ? seg.length : 0;
        if (segLen > 0) {
          bufferCtx.strokeStyle = colors[b];
          bufferCtx.beginPath();
          for (let s = 0; s < segLen; s += 4) {
            bufferCtx.moveTo(seg[s], seg[s + 1]);
            bufferCtx.lineTo(seg[s + 2], seg[s + 3]);
          }
          bufferCtx.stroke();
        }
      }
    }
  }

  private drawFrame() {
    if (!this.canvas || !this.ctx || !this.windGridData || !this.bufferA || !this.ctxA || !this.bufferB || !this.ctxB) return;

    const state = store.getState();
    const resMode = state.windCanvasResolution || 'high';
    const resScale = resMode === 'low' ? 0.5 : (resMode === 'med' ? 0.75 : 1.0);
    const batchMode = state.windColorBatching || 'high';

    const container = this.map.getContainer();
    const clientW = container.clientWidth || 1600;
    const clientH = container.clientHeight || 1000;
    const width = Math.max(320, Math.round(clientW * resScale));
    const height = Math.max(200, Math.round(clientH * resScale));

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.bufferA.width = width;
      this.bufferA.height = height;
      this.bufferB.width = width;
      this.bufferB.height = height;
      this.clearAllBuffers();
    }

    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());
    const vpWidth = viewportEast - viewportWest;

    if (vpWidth <= 0 || viewportNorth <= viewportSouth) return;

    const mercNorthY = latToMercatorY(viewportNorth);
    const mercSouthY = latToMercatorY(viewportSouth);
    const mercHeight = mercSouthY - mercNorthY;
    if (mercHeight <= 0) return;

    const coords: [[number, number], [number, number], [number, number], [number, number]] = [
      [viewportWest, viewportNorth],
      [viewportEast, viewportNorth],
      [viewportEast, viewportSouth],
      [viewportWest, viewportSouth]
    ];

    const canvasSource = this.map.getSource(this.sourceId) as any;
    if (canvasSource && typeof canvasSource.setCoordinates === 'function') {
      canvasSource.setCoordinates(coords);
    }

    // 1. Density, Speed, Size, Trail, Visibility and Playing based on Profile
    let targetDensity = 4500;
    let speedMultiplier = 0.15;
    let lineWidth = 1.5;
    let fadeAlpha = 0.022;
    let isVisible = true;
    let isPlaying = true;
    let isWhiteMode = false;

    if (this.profile === 'jet_stream') {
      const baseDensity = state.jetStreamDensity || 4500;
      if (state.jetStreamParticleDensityLevel === 'low') targetDensity = 2200;
      else if (state.jetStreamParticleDensityLevel === 'high') targetDensity = 7500;
      else targetDensity = baseDensity;

      speedMultiplier = state.jetStreamSpeed || 0.15;

      if (state.jetStreamParticleSize === 'fine') lineWidth = 1.0;
      else if (state.jetStreamParticleSize === 'bold') lineWidth = 2.5;
      else lineWidth = 1.5;

      if (state.jetStreamTrailLength === 'short') fadeAlpha = 0.055;
      else if (state.jetStreamTrailLength === 'long') fadeAlpha = 0.010;
      else fadeAlpha = 0.022;

      isVisible = state.jetStreamParticlesVisible !== false &&
                  state.showJetStreamWinds !== false &&
                  state.jetStreamDisplayMode !== 'raster';
      isPlaying = state.jetStreamParticlesPlaying !== false;
      isWhiteMode = state.jetStreamColorMode === 'white';
    } else {
      const baseDensity = state.windDensity || 4500;
      if (state.windParticleDensityLevel === 'low') targetDensity = 2200;
      else if (state.windParticleDensityLevel === 'high') targetDensity = 7500;
      else targetDensity = baseDensity;

      speedMultiplier = state.windSpeed || 0.15;

      if (state.windParticleSize === 'fine') lineWidth = 1.0;
      else if (state.windParticleSize === 'bold') lineWidth = 2.5;
      else lineWidth = 1.5;

      if (state.windTrailLength === 'short') fadeAlpha = 0.055;
      else if (state.windTrailLength === 'long') fadeAlpha = 0.010;
      else fadeAlpha = 0.022;

      isVisible = state.windParticlesVisible !== false;
      isPlaying = state.windParticlesPlaying !== false;
      isWhiteMode = state.windColorMode === 'white';
    }

    if (!isVisible) {
      this.ctx.clearRect(0, 0, width, height);
      if (canvasSource && typeof canvasSource.play === 'function') {
        canvasSource.play();
      }
      try {
        this.map.triggerRepaint();
      } catch (_e) {}
      return;
    }

    // Ping-Pong buffer rotation every 30 seconds
    const now = Date.now();
    const elapsed = now - this.cycleStartTime;
    const transitionStart = this.cycleDuration - this.crossfadeDuration;

    if (elapsed >= this.cycleDuration) {
      if (this.activeBuffer === 'A') {
        this.ctxA.clearRect(0, 0, width, height);
        this.particlesA.length = 0;
        this.activeBuffer = 'B';
      } else {
        this.ctxB.clearRect(0, 0, width, height);
        this.particlesB.length = 0;
        this.activeBuffer = 'A';
      }
      this.cycleStartTime = now;
    }

    const currentElapsed = now - this.cycleStartTime;
    const inTransition = currentElapsed >= transitionStart;

    if (!inTransition) {
      if (this.activeBuffer === 'A') {
        this.stepBuffer(
          this.ctxA, this.particlesA, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );
        this.ctx.clearRect(0, 0, width, height);
        this.ctx.drawImage(this.bufferA, 0, 0);
      } else {
        this.stepBuffer(
          this.ctxB, this.particlesB, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );
        this.ctx.clearRect(0, 0, width, height);
        this.ctx.drawImage(this.bufferB, 0, 0);
      }
    } else {
      const t = (currentElapsed - transitionStart) / this.crossfadeDuration;
      const alphaPrimary = Math.max(0, Math.min(1, 1.0 - t));
      const alphaIncoming = Math.max(0, Math.min(1, t));

      if (this.activeBuffer === 'A') {
        this.stepBuffer(
          this.ctxA, this.particlesA, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );
        if (this.particlesB.length === 0) {
          this.particlesB = this.createParticles(targetDensity);
        }
        this.stepBuffer(
          this.ctxB, this.particlesB, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );

        this.ctx.clearRect(0, 0, width, height);
        this.ctx.globalAlpha = alphaPrimary;
        this.ctx.drawImage(this.bufferA, 0, 0);
        this.ctx.globalAlpha = alphaIncoming;
        this.ctx.drawImage(this.bufferB, 0, 0);
        this.ctx.globalAlpha = 1.0;
      } else {
        this.stepBuffer(
          this.ctxB, this.particlesB, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );
        if (this.particlesA.length === 0) {
          this.particlesA = this.createParticles(targetDensity);
        }
        this.stepBuffer(
          this.ctxA, this.particlesA, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, batchMode
        );

        this.ctx.clearRect(0, 0, width, height);
        this.ctx.globalAlpha = alphaPrimary;
        this.ctx.drawImage(this.bufferB, 0, 0);
        this.ctx.globalAlpha = alphaIncoming;
        this.ctx.drawImage(this.bufferA, 0, 0);
        this.ctx.globalAlpha = 1.0;
      }
    }

    if (canvasSource && typeof canvasSource.play === 'function') {
      canvasSource.play();
    }
    try {
      this.map.triggerRepaint();
    } catch (_e) {}
  }

  private getWindAt(lon: number, lat: number) {
    if (!this.windGridData) return null;

    const west = this.windGridData.west;
    const east = this.windGridData.east;
    const south = this.windGridData.south;
    const north = this.windGridData.north;
    const isGlobal = (east - west) >= 359;

    let sampleLon = lon;
    if (isGlobal) {
      const span = east - west;
      sampleLon = ((((lon - west) % span) + span) % span) + west;
    } else {
      const gridCenter = (west + east) / 2;
      const worldOffset = Math.round((lon - gridCenter) / 360) * 360;
      sampleLon = lon - worldOffset;
    }

    const x_pct = (sampleLon - west) / (east - west);
    const y_pct = (lat - south) / (north - south);

    if (x_pct < 0 || x_pct > 1 || y_pct < 0 || y_pct > 1) return null;

    const gridW = this.windGridData.grid_width;
    const gridH = this.windGridData.grid_height;
    const u = this.windGridData.u;
    const v = this.windGridData.v;
    const gust = this.windGridData.gust || [];

    const col = x_pct * (gridW - 1);
    const row = y_pct * (gridH - 1);

    const c0 = Math.floor(col);
    const c1 = Math.min(c0 + 1, gridW - 1);
    const r0 = Math.floor(row);
    const r1 = Math.min(r0 + 1, gridH - 1);

    const idx00 = r0 * gridW + c0;
    const idx01 = r0 * gridW + c1;
    const idx10 = r1 * gridW + c0;
    const idx11 = r1 * gridW + c1;

    const u00 = u[idx00];
    const u01 = u[idx01];
    const u10 = u[idx10];
    const u11 = u[idx11];

    const v00 = v[idx00];
    const v01 = v[idx01];
    const v10 = v[idx10];
    const v11 = v[idx11];

    if (u00 === undefined || u01 === undefined || v00 === undefined || v11 === undefined) {
      return null;
    }

    const tc = col - c0;
    const tr = row - r0;

    let interpU = (1 - tr) * ((1 - tc) * u00 + tc * u01) + tr * ((1 - tc) * u10 + tc * u11);
    let interpV = (1 - tr) * ((1 - tc) * v00 + tc * v01) + tr * ((1 - tc) * v10 + tc * v11);

    let interpGust = 0;
    if (gust && gust.length > 0) {
      const g00 = gust[idx00];
      const g01 = gust[idx01];
      const g10 = gust[idx10];
      const g11 = gust[idx11];
      interpGust = (1 - tr) * ((1 - tc) * g00 + tc * g01) + tr * ((1 - tc) * g10 + tc * g11);
    } else {
      interpGust = Math.sqrt(interpU * interpU + interpV * interpV);
    }

    const speed = Math.sqrt(interpU * interpU + interpV * interpV);
    const state = store.getState();
    const mode = state.liveWeatherRtmaWindsMode || this.displayMode || 'average';
    const isGustMode = (mode === 'gusts');

    if (isGustMode && speed > 0.01) {
      const scale = interpGust / speed;
      interpU *= scale;
      interpV *= scale;
    }

    return { u: interpU, v: interpV, speed: isGustMode ? interpGust : speed };
  }

  private ensureLayerPosition() {
    if (!this.windGridData || !this.canvas) return;

    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());

    const coords: [[number, number], [number, number], [number, number], [number, number]] = [
      [viewportWest, viewportNorth],
      [viewportEast, viewportNorth],
      [viewportEast, viewportSouth],
      [viewportWest, viewportSouth]
    ];

    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'canvas',
        canvas: this.canvas,
        coordinates: coords,
        animate: true
      });
    } else {
      const canvasSource = this.map.getSource(this.sourceId) as any;
      if (canvasSource && typeof canvasSource.setCoordinates === 'function') {
        canvasSource.setCoordinates(coords);
      }
    }

    if (!this.map.getLayer(this.layerId)) {
      let insertBefore = undefined;
      const layers = this.map.getStyle()?.layers;
      if (layers) {
        const labelLayer = layers.find(l => l.type === 'symbol' || l.id.includes('label') || l.id.includes('place') || l.id.includes('symbol'));
        if (labelLayer) {
          insertBefore = labelLayer.id;
        }
      }

      this.map.addLayer({
        id: this.layerId,
        type: 'raster',
        source: this.sourceId,
        paint: {
          'raster-opacity': this.opacity
        }
      }, insertBefore);
    } else {
      this.updateLayerOpacity();
      try {
        let insertBefore = undefined;
        const layers = this.map.getStyle()?.layers;
        if (layers) {
          const labelLayer = layers.find(l => l.type === 'symbol' || l.id.includes('label') || l.id.includes('place') || l.id.includes('symbol'));
          if (labelLayer) {
            insertBefore = labelLayer.id;
          }
        }
        const layerIdx = layers?.findIndex(l => l.id === this.layerId) ?? -1;
        const targetIdx = insertBefore ? layers?.findIndex(l => l.id === insertBefore) ?? -1 : -1;
        if (layerIdx !== -1 && targetIdx !== -1 && layerIdx > targetIdx) {
          this.map.moveLayer(this.layerId, insertBefore);
        }
      } catch (_e) {}
    }
  }

  private updateLayerOpacity() {
    if (this.map.getLayer(this.layerId)) {
      this.map.setPaintProperty(this.layerId, 'raster-opacity', this.opacity);
    }
  }

  public clearCache(): void {
    this.currentGridUrl = '';
    this.windGridData = null;
    this.clear();
  }

  public clear(): void {
    this.stopAnimation();
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    if (this.map.getLayer(this.layerId)) this.map.removeLayer(this.layerId);
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
    this.clearAllBuffers();
    this.windGridData = null;
    this.currentGridUrl = '';
  }

  public destroy() {
    this.clear();
  }
}

export default CanvasWindRenderer;
